import React, { useState, useRef, useEffect, useCallback } from 'react';
import Waveform from './Waveform';
import { generateWaveformFromBlob } from '../../utils/waveform';
import '../../css/chats/AudioPlayer.css';

/**
 * Аудиоплеер для воспроизведения аудио в чате
 */
export default function AudioPlayer({ audioUrl, fileName, fileId, onDownload }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [volume, setVolume] = useState(1);
    const [prevVolume, setPrevVolume] = useState(1);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [waveform, setWaveform] = useState([]);
    const audioRef = useRef(null);
    const animationRef = useRef(null);
    const volumeWrapperRef = useRef(null);

    // Форматирование времени (мм:сс)
    const formatTime = (seconds) => {
        if (isNaN(seconds)) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Обработка загрузки метаданных
    const handleLoadedMetadata = () => {
        console.log('[AudioPlayer] Metadata loaded:', {
            duration: audioRef.current?.duration,
            readyState: audioRef.current?.readyState
        });
        setIsLoading(false);
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    // Обработка ошибки загрузки
    const handleLoadError = (e) => {
        console.error('[AudioPlayer] Load error:', e);
        setIsLoading(false);
        setError(true);
    };

    // Обновление текущего времени воспроизведения
    const updateTime = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
            animationRef.current = requestAnimationFrame(updateTime);
        }
    };

    // Обработка начала воспроизведения
    const handlePlay = () => {
        setIsPlaying(true);
        animationRef.current = requestAnimationFrame(updateTime);
    };

    // Обработка паузы
    const handlePause = () => {
        setIsPlaying(false);
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
    };

    // Обработка окончания воспроизведения
    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
    };

    // Переключение воспроизведения
    const togglePlay = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
    };

    // Изменение громкости
    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
        }
        // Запоминаем ненулевую громкость для restore после unmute
        if (newVolume > 0) {
            setPrevVolume(newVolume);
        }
    };

    // Mute / unmute
    const toggleMute = useCallback(() => {
        if (volume > 0) {
            setPrevVolume(volume);
            setVolume(0);
            if (audioRef.current) audioRef.current.volume = 0;
        } else {
            const restore = prevVolume || 1;
            setVolume(restore);
            if (audioRef.current) audioRef.current.volume = restore;
        }
    }, [volume, prevVolume]);

    // Переключение видимости слайдера громкости
    const toggleVolumeSlider = useCallback(() => {
        setShowVolumeSlider(prev => !prev);
    }, []);

    // Закрытие popover при клике вне области громкости
    useEffect(() => {
        if (!showVolumeSlider) return;
        const handleClickOutside = (e) => {
            if (volumeWrapperRef.current && !volumeWrapperRef.current.contains(e.target)) {
                setShowVolumeSlider(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showVolumeSlider]);

    // Иконка громкости в зависимости от уровня
    const renderVolumeIcon = () => {
        if (volume === 0) {
            // Muted
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
            );
        }
        if (volume <= 0.5) {
            // Low volume
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
            );
        }
        // High volume
        return (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
        );
    };

    // Очистка при размонтировании
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    // Установка src при изменении audioUrl
    useEffect(() => {
        if (audioRef.current && audioUrl) {
            audioRef.current.src = audioUrl;
            audioRef.current.load();
        }
    }, [audioUrl]);

    // Генерация waveform из загруженного аудио (через fetch blob URL)
    useEffect(() => {
        if (!audioUrl) return;
        let cancelled = false;
        setWaveform([]);
        fetch(audioUrl)
            .then(res => res.blob())
            .then(blob => generateWaveformFromBlob(blob, 50))
            .then(amps => {
                if (!cancelled) setWaveform(amps);
            })
            .catch(() => { /* waveform опционален */ });
        return () => { cancelled = true; };
    }, [audioUrl]);

    return (
        <div className="audio-player">
            {error ? (
                <div className="audio-player__error">
                    <span>⚠️ Ошибка загрузки аудио</span>
                    {fileName && (
                        <div>
                            <a
                                href={audioUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                onClick={onDownload ? (e) => { e.preventDefault(); onDownload(e); } : undefined}
                                className="audio-player__download-link"
                            >
                                Скачать файл
                            </a>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <audio
                        ref={audioRef}
                        src={audioUrl}
                        preload="metadata"
                        onLoadedMetadata={handleLoadedMetadata}
                        onError={handleLoadError}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        onEnded={handleEnded}
                    />

                    <div className="audio-player__controls">
                        <button
                            className="audio-player__play-btn"
                            onClick={togglePlay}
                            disabled={isLoading || error}
                            aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
                        >
                            {isLoading ? (
                                <span className="audio-player__loading">⏳</span>
                            ) : isPlaying ? (
                                <span className="audio-player__icon">⏸️</span>
                            ) : (
                                <span className="audio-player__icon">▶️</span>
                            )}
                        </button>

                        <div className="audio-player__progress-container">
                            <Waveform
                                amplitudes={waveform}
                                progress={duration > 0 ? currentTime / duration : 0}
                                onSeek={(fraction) => {
                                    if (audioRef.current && duration > 0) {
                                        const seekTime = fraction * duration;
                                        audioRef.current.currentTime = seekTime;
                                        setCurrentTime(seekTime);
                                    }
                                }}
                            />
                            <input
                                type="range"
                                className="audio-player__progress"
                                min="0"
                                max={duration || 0}
                                step="0.1"
                                value={currentTime}
                                onChange={(e) => {
                                    const seekTime = parseFloat(e.target.value);
                                    if (audioRef.current) {
                                        audioRef.current.currentTime = seekTime;
                                        setCurrentTime(seekTime);
                                    }
                                }}
                                aria-label="Прогресс воспроизведения"
                            />
                            <div className="audio-player__time">
                                <span className="audio-player__time-current">{formatTime(currentTime)}</span>
                                <span className="audio-player__time-separator">/</span>
                                <span className="audio-player__time-duration">{formatTime(duration)}</span>
                            </div>
                        </div>

                        <div className="audio-player__volume-wrapper" ref={volumeWrapperRef}>
                            <button
                                className="audio-player__volume-btn"
                                onClick={toggleVolumeSlider}
                                onDoubleClick={toggleMute}
                                aria-label={volume === 0 ? 'Включить звук' : 'Настроить громкость'}
                                title="Громкость"
                            >
                                {renderVolumeIcon()}
                            </button>
                            {showVolumeSlider && (
                                <div className="audio-player__volume-popover">
                                    <input
                                        type="range"
                                        className="audio-player__volume-slider"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={volume}
                                        onChange={handleVolumeChange}
                                        aria-label="Громкость"
                                    />
                                </div>
                            )}
                        </div>

                        <a
                            href={audioUrl}
                            download={fileName || 'audio'}
                            onClick={onDownload ? (e) => { e.preventDefault(); onDownload(e); } : undefined}
                            className="audio-player__download-btn"
                            title="Скачать"
                            aria-label="Скачать аудио"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        </a>
                    </div>

                    {fileName && (
                        <div className="audio-player__filename" title={fileName}>
                            {fileName}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
